import { CheckIcon, ClockIcon, Cross2Icon, DownloadIcon, ReloadIcon } from "@radix-ui/react-icons";
import { formatDistanceToNow } from "date-fns";
import { useState } from "react";
import { downloadFile } from "../../utils/download";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils/common";
import { getErrorMessage } from "@/lib/utils/errors";
import { removeJob as removeJobAction, type Job, type JobType } from "@/store/asyncJobs";
import { useAppDispatch } from "@/store/hooks";

const JOB_TYPE_LABELS: { readonly [K in JobType]: string } = {
  SUPPLY_CHAIN_LOT_EXPORT: "Lots Export",
} as const;

interface JobStatusItemProps {
  job: Job;
}

export function JobStatusItem({ job }: JobStatusItemProps) {
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const [isDownloading, setIsDownloading] = useState(false);

  const relativeTime = formatDistanceToNow(new Date(job.createdAt), { addSuffix: true });

  const { icon, label, color } = getStatusDetails(job.status);

  const handleDownload = async () => {
    if (!job.url) {
      toast({
        title: "Download failed",
        description: "No URL provided",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsDownloading(true);
      await downloadFile(job.url, job.fileName || "export.csv");
    } catch (error) {
      toast({
        title: "Download failed",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(removeJobAction(job.id));
  };

  return (
    <div
      className={cn("rounded-md border p-3 bg-card relative", job.status === "ERROR" && "border-destructive/40")}
      data-testid="job-status-item"
    >
      <div className="flex justify-between items-start">
        <div className="flex items-center gap-2">
          <div className={cn("p-1 rounded-full", color)}>{icon}</div>
          <div>
            <p className="font-medium text-sm">
              {JOB_TYPE_LABELS[job.type]}
              <span className="text-xs text-muted-foreground ml-2">{relativeTime}</span>
            </p>
            <p className="text-sm text-muted-foreground">{label}</p>
            {job.error ? <p className="text-xs text-destructive mt-1">{String(job.error)}</p> : null}
          </div>
        </div>

        <div className="flex gap-1">
          {job.status === "COMPLETED" && job.url ? (
            <Button onClick={handleDownload} disabled={isDownloading} variant="ghost" size="icon" title="Download">
              <DownloadIcon data-testid="download-icon" className={cn(isDownloading ? "animate-pulse" : "")} />
              <span className="sr-only">Download</span>
            </Button>
          ) : null}

          <Button onClick={handleDismiss} variant="ghost" size="icon" title="Dismiss">
            <Cross2Icon />
            <span className="sr-only">Dismiss</span>
          </Button>
        </div>
      </div>

      {job.fileName ? (
        <div className="mt-2 text-xs text-muted-foreground border-t pt-2">
          {job.fileName ? <p>File: {job.fileName}</p> : null}
        </div>
      ) : null}
    </div>
  );
}

function getStatusDetails(status: Job["status"]) {
  switch (status) {
    case "PENDING":
      return {
        icon: <ClockIcon className="h-4 w-4 text-amber-500" />,
        label: "Waiting to start...",
        color: "bg-amber-100 dark:bg-amber-950/30",
      };
    case "IN_PROGRESS":
      return {
        icon: <ReloadIcon className="h-4 w-4 text-blue-500 animate-spin" />,
        label: "Processing...",
        color: "bg-blue-100 dark:bg-blue-950/30",
      };
    case "COMPLETED":
      return {
        icon: <CheckIcon className="h-4 w-4 text-green-500" />,
        label: "Completed successfully",
        color: "bg-green-100 dark:bg-green-950/30",
      };
    case "ERROR":
      return {
        icon: <Cross2Icon className="h-4 w-4 text-red-500" />,
        label: "Failed to complete",
        color: "bg-red-100 dark:bg-red-950/30",
      };
    default:
      return {
        icon: <ClockIcon className="h-4 w-4" />,
        label: "Unknown status",
        color: "bg-gray-100 dark:bg-gray-800",
      };
  }
}
